/**
 * ClaimAnimation — cinematic 5.2-second wow sequence shown when the
 * user taps CLAIM. Replaces the static modal content with a full
 * theatrical reveal:
 *
 *   PHASE 1  ENGAGING   (0.0–0.6 s)  — circular ring spawns, status
 *                                       text "Engaging neural network…"
 *   PHASE 2  PROCESSING (0.6–2.7 s)  — progress 0→70%, status text
 *                                       cycles through 5 messages
 *   PHASE 3  SETTLING   (2.7–3.7 s)  — progress 70→100%, status
 *                                       "Settling to balance…"
 *   PHASE 4  SUCCESS    (3.7–5.2 s)  — big check icon scale-pop,
 *                                       20-particle confetti burst,
 *                                       3 expanding gold rings,
 *                                       "+X.XX USD₮" amount reveal
 *   DONE     (5.2 s)              — onComplete() → parent updates
 *                                       balance and closes modal
 *
 * Visual elements:
 *   - Circular SVG progress ring (276.46 px circumference, drop-shadow)
 *   - Centre percent digit (replaces with check on success)
 *   - Status message stack with Motion fade transitions
 *   - Linear progress bar at the bottom
 *   - Confetti particles (CSS keyframes with --dx/--dy css vars)
 *   - 3 expanding gold rings on success
 */
import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Check, Share2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useTelegramWebApp } from '@/hooks/useTelegramWebApp';
import { lunaApi } from '@/services/api';
import checkmarkBellSfx from '@/assets/sfx/checkmark-bell.mp3';
import checkmarkDongSfx from '@/assets/sfx/checkmark-dong.mp3';

type SubPhase = 'engaging' | 'processing' | 'settling' | 'success';

const STATUS_MESSAGES = [
  'Engaging neural network…',
  'Verifying transactions…',
  'Querying oracles…',
  'Validating merkle proof…',
  'Aggregating yield curves…',
  'Computing reward shares…',
  'Settling to balance…',
] as const;

const RING_CIRCUMFERENCE = 276.46; // 2 * π * 44

export interface ClaimBreakdownEntry {
  short: string;
  amount: number;
}

interface ClaimAnimationProps {
  amount: number;
  /** Breakdown of the total claim across the 7 businesses. Used in
   *  the success view so the user sees WHICH product contributed
   *  what — perfect for screenshots. */
  breakdown: ClaimBreakdownEntry[];
  /** У юзера есть ХОТЯ БЫ ОДИН PC-контракт? Если да — показываем
   *  подпись "от нод без Plasma Core", чтобы сумма не вводила в
   *  заблуждение: manual claim собирает только с non-PC нод. */
  hasPcContracts?: boolean;
  onComplete: () => void;
}

export function ClaimAnimation({ amount, breakdown: _breakdown, hasPcContracts = false, onComplete }: ClaimAnimationProps) {
  const { t } = useTranslation();
  const { haptic } = useTelegramWebApp();
  const [phase, setPhase] = useState<SubPhase>('engaging');
  const [progress, setProgress] = useState(0);
  const [statusIdx, setStatusIdx] = useState(0);
  const completedRef = useRef(false);

  // ── Phase machine ──────────────────────────────────────────────
  // We DON'T auto-close on success — the user must tap the Continue
  // button so they have time to take a screenshot and share it on
  // social media. The completion happens via handleContinue().
  useEffect(() => {
    const timers: number[] = [];
    haptic.medium();

    // engaging → processing
    timers.push(
      window.setTimeout(() => {
        setPhase('processing');
        haptic.light();
      }, 600),
    );

    // processing → settling
    timers.push(
      window.setTimeout(() => {
        setPhase('settling');
        haptic.light();
      }, 2700),
    );

    // settling → success (final state — modal stays here until user taps)
    timers.push(
      window.setTimeout(() => {
        setPhase('success');
        haptic.success();
        // SFX галочки — рандомно один из двух (мягкий fade уже в файле)
        try {
          const src = Math.random() < 0.5 ? checkmarkBellSfx : checkmarkDongSfx;
          const a = new Audio(src);
          a.volume = 0.6;
          void a.play().catch(() => {});
        } catch {
          /* ignore */
        }
      }, 3700),
    );

    return () => timers.forEach(clearTimeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleContinue = () => {
    if (completedRef.current) return;
    completedRef.current = true;
    haptic.light();
    onComplete();
  };

  // Share-to-Story: тянем сгенерированный PNG с бэка → шарим в TG Stories.
  const [shareLoading, setShareLoading] = useState(false);
  const [shareError, setShareError] = useState('');
  const handleShareToStory = async () => {
    if (shareLoading || amount < 0.001) return;
    setShareError('');
    setShareLoading(true);
    haptic.light();
    try {
      const res = await lunaApi.shareCard.generate(amount);
      const url = res.data.url;
      const shareUrl = res.data.share_url ?? 'https://t.me/moonraid_game_bot';
      // Telegram Mini App SDK: tg.shareToStory(media_url, options)
      // Доступно с TG Bot API 7.8+ (Telegram 11.6+).
      type WebAppShare = {
        shareToStory?: (
          mediaUrl: string,
          params?: {
            text?: string;
            widget_link?: { url: string; name?: string };
          },
        ) => void;
      };
      const tg = (window as unknown as { Telegram?: { WebApp?: WebAppShare } })
        .Telegram?.WebApp;
      if (tg?.shareToStory) {
        // text — overlay-подпись с реф-ссылкой в конце (TG Stories
        // не поддерживает HTML-ссылки в overlay, поэтому даём URL
        // plain-текстом — зритель скопирует или прочтёт). widget_link
        // дублирует это кликабельным sticker'ом.
        tg.shareToStory(url, {
          text: `LUNA PROTOKOL — пассивный доход на USDT 🚀\n${shareUrl}`,
          widget_link: {
            url: shareUrl,
            name: 'LUNA PROTOKOL',
          },
        });
      } else {
        // Браузер / старый TG → открываем PNG в новой вкладке
        window.open(url, '_blank', 'noopener,noreferrer');
      }
    } catch (err) {
      const msg = (err as { message?: string }).message ?? 'Share failed';
      setShareError(msg);
    } finally {
      setShareLoading(false);
    }
  };

  // ── Progress 0 → 100 over 3700 ms (ease-out cubic) ─────────────
  useEffect(() => {
    const start = Date.now();
    const totalMs = 3700;
    const id = window.setInterval(() => {
      const elapsed = Date.now() - start;
      const t = Math.min(1, elapsed / totalMs);
      const eased = 1 - Math.pow(1 - t, 3);
      setProgress(eased * 100);
      if (t >= 1) clearInterval(id);
    }, 30);
    return () => clearInterval(id);
  }, []);

  // ── Cycle status messages every ~430 ms while not in success ───
  useEffect(() => {
    if (phase === 'success') return;
    const id = window.setInterval(() => {
      setStatusIdx((i) => Math.min(i + 1, STATUS_MESSAGES.length - 1));
    }, 430);
    return () => clearInterval(id);
  }, [phase]);

  const dashoffset = RING_CIRCUMFERENCE - (progress / 100) * RING_CIRCUMFERENCE;
  const isSuccess = phase === 'success';

  return (
    <div
      className="absolute inset-0 z-10"
      // No `contain: paint` — it would clip the expanding success
      // rings to the modal bounds. Rings are allowed to extend beyond.
    >
      {/* Layered absolute content — every element has a fixed slot,
          so nothing can shift when phase changes from processing → success. */}

      {/* ─── 3 expanding rings — centred ON the check icon (top 72+90=162).
              Сдвинут вниз чтобы halo чек-круга и processing-ring не
              упирались в верхний край модалки. ─── */}
      {isSuccess && (
        <div
          className="absolute pointer-events-none flex items-center justify-center"
          style={{ top: 72, left: 0, right: 0, height: 180 }}
        >
          <div className="claim-success-ring claim-success-ring-1" />
          <div className="claim-success-ring claim-success-ring-2" />
          <div className="claim-success-ring claim-success-ring-3" />
        </div>
      )}

      {/* ─── Confetti particles — emitted FROM the check icon centre
              (top 52+90=142, сдвинулись вместе с ring-container) ─── */}
      {isSuccess && (
        <div
          className="absolute pointer-events-none"
          style={{
            top: 162,
            left: '50%',
            width: 0,
            height: 0,
          }}
        >
          {Array.from({ length: 18 }).map((_, i) => {
            const angle = (i / 18) * Math.PI * 2;
            const distance = 130;
            const dx = Math.cos(angle) * distance;
            const dy = Math.sin(angle) * distance;
            return (
              <span
                key={i}
                className="claim-particle"
                style={
                  {
                    '--dx': `${dx}px`,
                    '--dy': `${dy}px`,
                    animationDelay: `${i * 0.02}s`,
                  } as CSSProperties
                }
              />
            );
          })}
        </div>
      )}

      {/* ─── Circular progress ring (fixed slot at top) ─── */}
      <div
        className="absolute"
        style={{
          top: 72,
          left: '50%',
          transform: 'translateX(-50%)',
          width: 180,
          height: 180,
        }}
      >
        {/* overflow:visible нужен чтобы drop-shadow вокруг прогресс-
             круга не обрезался viewBox'ом SVG и halo свободно
             разливалось вокруг ring'а (иначе свечение упирается
             в "невидимую стену" по краям SVG). */}
        <svg viewBox="0 0 100 100" className="absolute inset-0 -rotate-90" style={{ overflow: 'visible' }}>
          <circle
            cx="50"
            cy="50"
            r="44"
            fill="none"
            stroke="var(--glass-stroke)"
            strokeWidth="2"
          />
          <circle
            cx="50"
            cy="50"
            r="44"
            fill="none"
            stroke="var(--accent-primary)"
            strokeWidth="3"
            strokeLinecap="round"
            strokeDasharray={RING_CIRCUMFERENCE}
            strokeDashoffset={dashoffset}
            style={{
              transition: 'stroke-dashoffset 0.08s linear',
              filter: 'drop-shadow(0 0 6px var(--accent-primary))',
            }}
          />
        </svg>

        {/* Centre slot — percent OR check icon. Both have the same
            container size so swapping doesn't push anything. */}
        <div className="absolute inset-0 flex items-center justify-center">
          {!isSuccess ? (
            <div className="flex flex-col items-center justify-center" style={{ height: 88, width: 88 }}>
              <div className="font-display text-[42px] font-extrabold text-[var(--text-primary)] tabular-nums leading-none">
                {Math.round(progress)}
                <span className="text-[20px] text-[var(--text-muted)] font-extrabold">
                  %
                </span>
              </div>
              <div className="font-mono text-[8px] uppercase tracking-[0.22em] text-[var(--text-muted)] mt-1.5">
                Processing
              </div>
            </div>
          ) : (
            <div className="claim-success-check" style={{ height: 88, width: 88 }}>
              <div
                className="h-[88px] w-[88px] rounded-full flex items-center justify-center"
                style={{
                  background:
                    'linear-gradient(180deg, var(--accent-primary), color-mix(in oklab, var(--accent-primary) 60%, var(--accent-secondary)))',
                  boxShadow:
                    '0 22px 60px -10px color-mix(in oklab, var(--accent-primary) 80%, transparent), inset 0 2px 0 0 rgba(255,255,255,0.6)',
                }}
              >
                <Check size={48} strokeWidth={3.2} className="text-[#0a0a0f]" />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ─── Status text (fixed slot below ring) ─── */}
      <div
        className="absolute text-center"
        style={{ top: 274, left: 0, right: 0, height: 18 }}
      >
        <AnimatePresence mode="wait" initial={false}>
          <motion.p
            key={isSuccess ? 'done' : statusIdx}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.28, ease: [0.32, 0.72, 0, 1] }}
            className="absolute inset-0 font-mono text-[10px] uppercase tracking-[0.2em] text-[var(--text-secondary)] font-bold"
          >
            {isSuccess ? '✓ Claim complete' : STATUS_MESSAGES[statusIdx]}
          </motion.p>
        </AnimatePresence>
      </div>

      {/* ─── Amount reveal (fixed slot) ─── */}
      <div
        className="absolute flex items-baseline justify-center gap-1"
        style={{
          top: 304,
          left: 0,
          right: 0,
          opacity: isSuccess ? 1 : 0.55,
          transition: 'opacity 0.4s ease-out',
        }}
      >
        <span className="font-display text-[38px] font-extrabold text-[var(--accent-primary)] tracking-tight tabular-nums">
          +{amount.toFixed(3)}
        </span>
        <span className="text-[14px] text-[var(--text-muted)] font-extrabold">
          USDT
        </span>
      </div>

      {/* Подпись — только если у юзера есть PC-контракты, чтобы не
          путать: manual claim собирает только non-PC ноды. */}
      {hasPcContracts && (
        <div
          className="absolute text-center"
          style={{
            top: 350,
            left: 0,
            right: 0,
            opacity: isSuccess ? 0.8 : 0,
            transition: 'opacity 0.4s ease-out',
          }}
        >
          <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--text-muted)]">
            {t('claim.manualOnlyHint')}
          </span>
        </div>
      )}

      {/* ─── Slot at top 354 — progress bar during processing,
              breakdown grid during success. Same fixed slot so the
              swap doesn't shift anything below. ─── */}
      <div
        className="absolute"
        style={{ left: 24, right: 24, top: 354, height: 140 }}
      >
        {/* Processing view: thin progress bar + percent label */}
        {!isSuccess && (
          <>
            <div className="h-[4px] rounded-full overflow-hidden bg-[var(--glass-stroke)] relative">
              <div
                className="h-full rounded-full"
                style={{
                  width: `${progress}%`,
                  background:
                    'linear-gradient(90deg, var(--accent-primary), var(--accent-secondary))',
                  transition: 'width 0.08s linear',
                  boxShadow: '0 0 8px var(--accent-primary)',
                }}
              />
            </div>
            <p className="mt-2 font-mono text-[8px] uppercase tracking-[0.2em] text-[var(--text-disabled)] text-center">
              {Math.round(progress)}% complete
            </p>
          </>
        )}

        {/* Success breakdown карточка удалена по запросу админа
            2026-04-21 — при одном ticked-слоте она дублирует сумму
            сверху, а у большинства юзеров активен 1 слот за цикл.
            Если нужно будет вернуть breakdown — восстановить из git. */}
      </div>

      {/* ─── Action buttons — appear only after success ─── */}
      <div
        className="absolute"
        style={{
          left: 24,
          right: 24,
          bottom: 149,
          opacity: isSuccess ? 1 : 0,
          transform: isSuccess ? 'translateY(0)' : 'translateY(8px)',
          transition: 'opacity 0.5s ease-out 0.4s, transform 0.5s ease-out 0.4s',
          pointerEvents: isSuccess ? 'all' : 'none',
        }}
      >
        {/* Share to Story — primary, accent gradient */}
        <button
          type="button"
          onClick={handleShareToStory}
          disabled={shareLoading || amount < 0.001}
          className="w-full rounded-[var(--radius-pill)] py-3.5 font-display text-[13px] font-extrabold tracking-[0.16em] uppercase text-[#0a0a0f] flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          style={{
            background:
              'linear-gradient(180deg, var(--accent-primary), color-mix(in oklab, var(--accent-primary) 65%, var(--accent-secondary)))',
            boxShadow:
              '0 18px 36px -10px color-mix(in oklab, var(--accent-primary) 70%, transparent), inset 0 1px 0 0 rgba(255,255,255,0.55)',
          }}
        >
          <Share2 size={15} strokeWidth={2.4} />
          Поделиться в сторис
        </button>
        {/* Loader — отдельной строкой ПОД кнопкой, чтобы не ломать кнопку */}
        {shareLoading && (
          <p className="mt-1.5 text-center text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--accent-primary)]">
            Готовим
            <span className="luna-dots">
              <span>.</span><span>.</span><span>.</span>
            </span>
          </p>
        )}
        {shareError && !shareLoading && (
          <p className="mt-1.5 text-center text-[10px] text-[var(--status-negative)]">
            {shareError}
          </p>
        )}

        {/* Continue — secondary, glass.
            data-no-sound: глушим глобальный uiTap чтобы он не
            перебивал claim_success (на iOS параллельные Audio
            режут один в пользу другого). */}
        <button
          type="button"
          onClick={handleContinue}
          data-no-sound="true"
          className="mt-2 w-full rounded-[var(--radius-pill)] py-3 font-display text-[12px] font-extrabold tracking-[0.16em] uppercase text-[var(--text-primary)]"
          style={{
            background:
              'linear-gradient(180deg, var(--glass-fill-strong), var(--glass-fill))',
            border: '1px solid var(--glass-stroke-strong)',
            boxShadow:
              'inset 0 1px 0 0 rgba(255,255,255,0.18), 0 8px 24px -8px rgba(0,0,0,0.4)',
          }}
        >
          Continue
        </button>
      </div>
    </div>
  );
}
